import { useDebounce } from '@vueuse/core'
import { useFuse } from '@vueuse/integrations/useFuse.mjs'
import { computed, type Ref } from 'vue'
import type { HubFile } from './types'

type UseHubFileSearchOptions = {
  searchTerm: Ref<string>
  files: Ref<HubFile[]>
}

export function useHubFileSearch({ searchTerm, files }: UseHubFileSearchOptions) {
  // When searchTerm is empty, we don't want to debounce for better UX
  const debounceTime = computed(() => (searchTerm.value ? 150 : 0))
  const debouncedTerm = useDebounce(searchTerm, debounceTime)

  // Fuzzy search works fairly unpredictably, it may be better to use a string subsequence matching
  const fuse = useFuse(debouncedTerm, files, {
    matchAllWhenSearchEmpty: true,

    fuseOptions: {
      isCaseSensitive: false,
      ignoreLocation: true, // otherwise it wouldn't search past first 60 characters
      threshold: 0.3, // arbitrary treshold that found fairly good results but excludes irrelevant ones
      keys: ['fields.path.manualValue', 'fileName', 'fields.summary.toolValue'],
    },
  })

  /** Files that match current search term */
  const filteredFiles = computed<HubFile[]>(() => fuse.results.value.map((result) => result.item))

  return {
    filteredFiles,
    debouncedSearchTerm: debouncedTerm,
  }
}
